import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { pprwAPI } from '../../services/api';

const KEEP = '__keep__';
const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
const TIMEZONES = ['IST', 'BST', 'GMT', 'PST', 'PDT'];
const SOLUTION_AREAS = ['AI Business Solutions', 'Cloud and AI Platforms', 'Security'];
const DELIVERY_STATUSES = ['Scheduled', 'Upcoming', 'In Progress', 'Completed', 'On Hold', 'Cancelled'];
const CONTENT_READINESS = ['Not Started', 'In Progress', 'Delayed', 'Ready'];
const CONTRACT_STATUSES = ['To Be Initiated', 'Initiated', 'Signed'];
const SHARE_STATUSES = ['To be shared', 'Shared'];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
    {children}
  </div>
);

const KeepSelect = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value={KEEP}>— Keep unchanged —</SelectItem>
      {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

// Bulk-edit the common/logistics fields of the selected PPRW sessions.
export function BulkEditDialog({ open, onClose, sessionIds = [], onSaved }) {
  const [form, setForm] = useState({
    delivery_status: KEEP, time_zone: KEEP, solution_area: KEEP, quarter: KEEP,
    content_readiness_status: KEEP, lab_readiness_status: KEEP,
    trainer_contract_status: KEEP, trainer_content_status: KEEP, lab_access_status: KEEP,
    trainer_1: '', trainer_2: '', po_number: '', start_date: '', end_date: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    const fields = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== KEEP && v !== '' && v != null) fields[k] = v;
    });
    if (Object.keys(fields).length === 0) { toast.error('Choose at least one field to update'); return; }
    setSaving(true);
    try {
      const res = await pprwAPI.bulkUpdate(sessionIds, fields);
      toast.success(`Updated ${res.updated} session${res.updated !== 1 ? 's' : ''}`);
      onSaved(); onClose();
    } catch (_e) { toast.error('Bulk update failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pprw-bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Edit {sessionIds.length} Session{sessionIds.length !== 1 ? 's' : ''}</DialogTitle>
          <DialogDescription>Only the fields you change are applied. Leave a field as “Keep unchanged” (or blank) to skip it.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <Field label="Delivery Status"><KeepSelect value={form.delivery_status} onChange={v => set('delivery_status', v)} options={DELIVERY_STATUSES} testId="bulk-status-select" /></Field>
          <Field label="Time Zone"><KeepSelect value={form.time_zone} onChange={v => set('time_zone', v)} options={TIMEZONES} testId="bulk-tz-select" /></Field>
          <Field label="Solution Area"><KeepSelect value={form.solution_area} onChange={v => set('solution_area', v)} options={SOLUTION_AREAS} testId="bulk-solution-area-select" /></Field>
          <Field label="Quarter"><KeepSelect value={form.quarter} onChange={v => set('quarter', v)} options={QUARTERS} testId="bulk-quarter-select" /></Field>
          <Field label="Trainer 1"><Input value={form.trainer_1} onChange={e => set('trainer_1', e.target.value)} placeholder="Leave blank to keep" data-testid="bulk-trainer1-input" /></Field>
          <Field label="Trainer 2"><Input value={form.trainer_2} onChange={e => set('trainer_2', e.target.value)} placeholder="Leave blank to keep" data-testid="bulk-trainer2-input" /></Field>
          <Field label="Start Date"><Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} data-testid="bulk-start-date-input" /></Field>
          <Field label="End Date"><Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} data-testid="bulk-end-date-input" /></Field>
          <Field label="PO Number"><Input value={form.po_number} onChange={e => set('po_number', e.target.value)} placeholder="Leave blank to keep" data-testid="bulk-po-input" /></Field>
          <Field label="Content Readiness"><KeepSelect value={form.content_readiness_status} onChange={v => set('content_readiness_status', v)} options={CONTENT_READINESS} testId="bulk-content-readiness-select" /></Field>
          <Field label="Lab Readiness"><KeepSelect value={form.lab_readiness_status} onChange={v => set('lab_readiness_status', v)} options={CONTENT_READINESS} testId="bulk-lab-readiness-select" /></Field>
          <Field label="Contract Status"><KeepSelect value={form.trainer_contract_status} onChange={v => set('trainer_contract_status', v)} options={CONTRACT_STATUSES} testId="bulk-contract-select" /></Field>
          <Field label="Content Shared"><KeepSelect value={form.trainer_content_status} onChange={v => set('trainer_content_status', v)} options={SHARE_STATUSES} testId="bulk-content-shared-select" /></Field>
          <Field label="Lab Access"><KeepSelect value={form.lab_access_status} onChange={v => set('lab_access_status', v)} options={SHARE_STATUSES} testId="bulk-lab-access-select" /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="bulk-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Applying…</> : `Apply to ${sessionIds.length}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BulkEditDialog;
